/*
 * Copyright (C) 2023 to Present Applause App Quality, Inc. All rights reserved.
 */

import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, Inject, OnDestroy, OnInit, Renderer2, RendererStyleFlags2, SecurityContext, ViewContainerRef } from '@angular/core';
import { NGF_TOOLTIP_CONTENT, NgfTooltipContent } from '@ng-falcon/ngf-tooltip/ngf-tooltip-content/ngf-tooltip-content';
import { NGF_TOOLTIP_STYLES, NgfTooltipStyles } from '@ng-falcon/ngf-tooltip/ngf-tooltip-content/ngf-tooltip-styles';
import {DomSanitizer} from "@angular/platform-browser";

// A highlighted run may reach the tooltip either as the `.ql-bg-yellow` class (rich text rendered
// via NgfRichTextViewerComponent, which converts the inline style to this class before
// sanitizing -- see its postProcessing()) or still as the raw inline style (content that skips
// that conversion, e.g. a plain string/HTMLElement tooltip). We only ever author yellow highlights
// as 'yellow' or '#FFFF00' (see NgfRichTextEditorComponent.sanitizeButPreserveHighlights()) --
// the browser normalizes the hex form to 'rgb(255, 255, 0)' when read back via `element.style`.
const YELLOW_BACKGROUND_VALUES = new Set(['yellow', 'rgb(255, 255, 0)']);

export function isYellowHighlight(element: HTMLElement): boolean {
  return element.classList.contains('ql-bg-yellow')
    || YELLOW_BACKGROUND_VALUES.has(element.style.backgroundColor.trim().toLowerCase());
}

@Component({
    selector: 'ngf-tooltip-content',
    imports: [],
    templateUrl: './ngf-tooltip-content.component.html',
    styleUrls: ['./ngf-tooltip-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('tooltip', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(12px) scale(0.8)' }),
                animate('0.2s ease-out', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
            ])
        ])
    ]
})
export class NgfTooltipContentComponent implements OnInit, OnDestroy {

  private yellowHighlightObserver: MutationObserver | null = null;

  constructor(private elementRef: ElementRef<HTMLElement>,
              private renderer: Renderer2,
              private vcr: ViewContainerRef,
              private readonly sanitizer: DomSanitizer,
              @Inject(NGF_TOOLTIP_CONTENT) private content: NgfTooltipContent,
              @Inject(NGF_TOOLTIP_STYLES) private styles: NgfTooltipStyles | null) {}

  @HostBinding('@tooltip')
  tooltip = true;

  ngOnInit(): void {
    this.renderer.setStyle(this.elementRef.nativeElement, 'max-width', this.styles?.maxWidth ?? '50ch');
    if (this.styles?.textAlign) {
      this.renderer.setStyle(this.elementRef.nativeElement, 'text-align', this.styles.textAlign);
    }

    this.observeYellowHighlights();

    if (NgfTooltipContent.isTemplate(this.content) || NgfTooltipContent.isTemplateWithContext(this.content)) {
      const {template, context} = NgfTooltipContent.isTemplate(this.content)
        ? {template: this.content, context: null}
        : this.content;

      // NOTE: we run the sanitizer when creating a tooltip
      // from a string or HTMLElement, but we are probably safe
      // to skip it when creating a tooltip from a template,
      // as there should be no externally-defined content involved.
      const embeddedViewRef = this.vcr.createEmbeddedView(template, context);

      // Angular is creating nodes and adding then below element from ViewContainerRef
      // aka created nodes are siblings of this component
      // therefore we have to manually append each created node as child of this component
      embeddedViewRef.rootNodes.forEach(node => {
        this.elementRef.nativeElement.appendChild(node);
      });
    } else if (NgfTooltipContent.isHTMLElement(this.content)) {
      this.elementRef.nativeElement.innerHTML =  this.sanitizer.sanitize(SecurityContext.HTML, this.content.innerHTML);
    } else if (NgfTooltipContent.isString(this.content)) {
      this.elementRef.nativeElement.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML, this.content);
    }
  }

  ngOnDestroy(): void {
    this.yellowHighlightObserver?.disconnect();
  }

  // A yellow highlight is unreadable against this tooltip's own white-on-black styling (both use
  // "!important", including on content nested in child components like ngf-rich-text-viewer, so a
  // plain CSS override can't reliably win the cascade) — force it to black-on-white instead via
  // inline styles with the Important flag, which always wins. Content can arrive as a nested
  // Angular component (template branch) that renders asynchronously, so a MutationObserver is used
  // instead of a one-time scan.
  private observeYellowHighlights(): void {
    const fixHighlights = () => {
      this.elementRef.nativeElement.querySelectorAll<HTMLElement>('*').forEach(element => {
        if (!isYellowHighlight(element)) {
          return;
        }

        this.renderer.setStyle(element, 'background-color', 'white', RendererStyleFlags2.Important);
        this.renderer.setStyle(element, 'color', 'black', RendererStyleFlags2.Important);

        // Other formatting (bold/italic/underline/strike) can wrap the highlighted text in its
        // own nested tags (e.g. `<strong class="ql-bg-yellow"><em><u>text</u></em></strong>`).
        // This host's own `* { color: white !important }` rule matches those nested tags
        // directly, which wins over the black we just set on their ancestor (a directly-matching
        // rule always beats an inherited value, regardless of !important on either side) — so
        // they must be fixed individually too.
        element.querySelectorAll<HTMLElement>('*').forEach(nested => {
          this.renderer.setStyle(nested, 'color', 'black', RendererStyleFlags2.Important);
        });
      });
    };

    // run once to fix any existing highlights, then observe for future changes
    fixHighlights();
    this.yellowHighlightObserver = new MutationObserver(fixHighlights);
    this.yellowHighlightObserver.observe(this.elementRef.nativeElement, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style']});
  }

}
